// Assign a therapist to a booking nobody has taken (Cal-02, 03): who is free
// and trained for it, the booking's own hub first, and why everyone else is
// not available (calendar-board.assign.ts). Pick one, then Assign.
//
// ⚠ In the demo, Assign changes the screen only, with Undo
// (calendar.mock-edits.ts). Cross-hub assignment is shown as the design draws
// it and is an open question before the real action exists.

import { CheckIcon } from "@phosphor-icons/react";
import { Button } from "@bednbody/ui/components/button";
import { cn } from "@bednbody/ui/lib/utils";
import { serviceLabel, spanLabel } from "./calendar-board";
import type { AssignOptions } from "./calendar-board.assign";
import { bookingKindLabel, PHASE_LABEL } from "./calendar-board.labels";
import { StaffAvatar } from "./CalendarIcons";
import { DetailFrame, ListHead } from "./CalendarPanelDetails";

export function AssignDetail({
  options,
  hubName,
  pickedId,
  onPick,
  onAssign,
  onBack,
  onOpenDispatch,
}: {
  options: AssignOptions;
  hubName: (hubId: string) => string;
  pickedId: string | null;
  onPick: (staffId: string) => void;
  onAssign: (staffId: string) => void;
  /** Real data: the calendar cannot save an assignment yet, Dispatch can. */
  onOpenDispatch?: () => void;
  onBack: () => void;
}) {
  const { session, candidates, blocked } = options;
  const picked = candidates.find(({ row }) => row.staffId === pickedId)?.row ?? null;
  return (
    <DetailFrame
      actions={
        <>
          {onOpenDispatch ? (
            <Button className="flex-1" onClick={onOpenDispatch}>
              Assign in Dispatch
            </Button>
          ) : (
            <Button
              className="flex-1"
              disabled={!picked}
              onClick={() => picked && onAssign(picked.staffId)}
            >
              {picked ? `Assign ${picked.name}` : "Pick a therapist"}
            </Button>
          )}
          <Button onClick={onBack} variant="outline">
            Cancel
          </Button>
        </>
      }
      meta={`${hubName(session.hubId)} hub · ${bookingKindLabel(session.bookingKind)} · ${session.ref}`}
      onBack={onBack}
      pill={{ label: PHASE_LABEL.unassigned, tone: "danger" }}
      subtitle={`${serviceLabel(session)} · ${spanLabel(session)}`}
      title="Assign a therapist"
    >
      <div className="space-y-1.5">
        <ListHead>Free and trained for {session.serviceName}</ListHead>
        {candidates.length === 0 ? (
          <p className="px-1 text-sm text-destructive">Nobody is free and trained for this time.</p>
        ) : (
          <ul className="space-y-1.5">
            {candidates.map(({ row, sameHub }) => {
              const on = row.staffId === pickedId;
              return (
                <li key={row.staffId}>
                  <button
                    aria-pressed={on}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                      on
                        ? "bg-primary/10 ring-2 ring-inset ring-primary"
                        : "bg-muted/40 hover:bg-muted",
                    )}
                    onClick={() => onPick(row.staffId)}
                    type="button"
                  >
                    <StaffAvatar role={row.role} status={row.status} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground">
                        {row.name}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {sameHub ? `${row.hubName} hub` : `Other hub · ${row.hubName}`}
                        {row.clockedIn ? "" : " · not clocked in"}
                      </span>
                    </span>
                    {on ? <CheckIcon className="size-5 text-primary" weight="bold" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {blocked.length > 0 ? (
        <div className="space-y-1">
          <ListHead>Not available</ListHead>
          <ul className="space-y-1 px-1">
            {blocked.map(({ row, reason }) => (
              <li className="flex justify-between gap-3 text-sm" key={row.staffId}>
                <span className="font-medium text-muted-foreground">{row.name}</span>
                <span className="text-right text-muted-foreground/80">{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </DetailFrame>
  );
}
